import {
  EndOfLifeApi,
  EndOfLifeResponse,
  EolFacts,
} from '@mercedes-benz/endoflife-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { TechInsightsApi } from '@mercedes-benz/tech-insights-common';

/**
 * Client for fetching end-of-life product information for entities.
 * Utilizes the TechInsights API to retrieve relevant data.
 *
 * @see {@link https://endoflife.data} for more information regarding end-of-life data.
 * @see {@link https://mercedes-benz.ghe.com/dh-io-techdocs/end-of-life-data} Mercedes-Benz end-of-life data and automations
 */
export class EndOfLifeClient implements EndOfLifeApi {
  constructor(private readonly techInsightsApi: TechInsightsApi<any>) {}

  /**
   * Fetches end-of-life products for a given entity.
   *
   * @param kind - The kind of the entity (e.g., Component, API).
   * @param namespace - The namespace of the entity.
   * @param name - The name of the entity.
   * @returns A promise that resolves to an EndOfLifeResponse or undefined if no data is found.
   */
  async getProducts(
    kind: string,
    namespace: string,
    name: string,
  ): Promise<EndOfLifeResponse | undefined> {
    const entityRef = stringifyEntityRef({ kind, namespace, name });

    const response = await this.techInsightsApi.factsLake().v1.getLatestFacts({
      providerSlugs: 'eol',
      entityRef,
      aggregation: 'FLAT',
    });

    if (response.status === 200) {
      const facts = response.data as any as EolFacts;
      const { eolData, ...overview } = facts;

      const products =
        eolData?.map(product => {
          return {
            product: product['eol-name'],
            cycle: product['eol-version'],
            releaseDate: product.releaseDate,
            lts: product.ltsFrom ?? product.isLts ?? null,
            support: product.eoasFrom ?? product.isEoas ?? null,
            extendedSupport: product.eoesFrom ?? product.isEoes ?? null,
            eol: product.eolFrom ?? product.isEol ?? null,
            latest: product.latest?.name ?? null,
            latestReleaseDate: product.latest?.date ?? null,
            link: 'https://mercedes-benz.ghe.com/dh-io-techdocs/end-of-life-data/tree/main/releases',
            discontinued: product.isDiscontinued ?? null,
          };
        }) ?? [];
      return {
        products,
        overview,
      };
    }
    return undefined;
  }
}
